/**
 * Bundle 142 - IncidentDriftBanner.
 *
 * Reads /api/admin/_debug/incident-registry-parity (Bundle 140).
 * When drift is non-empty, renders a red banner at the top of
 * /admin/incidents listing the endpoints without a UI tile. Also
 * exposes the current deployed source hashes so operators can
 * confirm the FE artifact they are looking at matches the code in
 * the repo.
 *
 * Third-generation guardrail for the "tile ships without button"
 * regression class (Bundle 132, Bundle 140 both attempted). This
 * one is USER-VISIBLE, not just a CI test.
 *
 * Guardrails: no em/en dashes; no eslint-disable; no photo/image
 * in HTML alt attributes.
 */
import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { AlertOctagon, CheckCircle2, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const IncidentDriftBanner = () => {
  const [parity, setParity] = useState(null);
  const [deployed, setDeployed] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api.get('/admin/_debug/incident-registry-parity')
      .then(({ data }) => setParity(data))
      .catch(() => setParity(null));
    api.get('/admin/_debug/deployed-bundle-hash')
      .then(({ data }) => setDeployed(data))
      .catch(() => setDeployed(null));
  }, []);

  if (!parity) return null;

  const hasDrift = !!parity.drift;
  const missingPanel = parity.missing_from_panel || [];
  const missingRegistry = parity.missing_from_registry || [];

  const copyDiag = async () => {
    const blob = JSON.stringify({parity, deployed}, null, 2);
    try {
      await navigator.clipboard.writeText(blob);
      toast.success('Diagnostic copied to clipboard.');
    } catch (_) {
      toast.error('Copy failed');
    }
  };

  return (
    <div
      className={`rounded-lg border px-4 py-3 mb-4 ${
        hasDrift
          ? 'bg-red-50 border-red-300 text-red-900'
          : 'bg-green-50 border-green-200 text-green-900'
      }`}
      data-testid="incident-drift-banner"
    >
      <div className="flex items-start gap-3">
        {hasDrift ? (
          <AlertOctagon className="h-5 w-5 mt-0.5 shrink-0" />
        ) : (
          <CheckCircle2 className="h-5 w-5 mt-0.5 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div
            className="font-semibold text-sm"
            data-testid="incident-drift-banner-title"
          >
            {hasDrift
              ? `Tile drift detected: ${missingPanel.length + missingRegistry.length} discrepancy(ies)`
              : 'Incident registry parity: OK'}
          </div>
          {hasDrift ? (
            <div className="mt-1 text-xs space-y-1">
              {missingPanel.length ? (
                <div data-testid="incident-drift-missing-panel">
                  Backend endpoints without a UI tile:{' '}
                  <span className="font-mono">
                    {missingPanel.join(', ')}
                  </span>
                </div>
              ) : null}
              {missingRegistry.length ? (
                <div data-testid="incident-drift-missing-registry">
                  UI tiles without a backend registry entry:{' '}
                  <span className="font-mono">
                    {missingRegistry.join(', ')}
                  </span>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              className="mt-0.5 text-xs opacity-80"
              data-testid="incident-drift-banner-ok-copy"
            >
              {parity.registered_paths?.length ?? 0} endpoints,{' '}
              {parity.panel_paths?.length ?? 0} tiles. Every backend
              action has a matching button.
            </div>
          )}

          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            className="text-xs mt-2 underline"
            data-testid="incident-drift-toggle-diagnostic"
          >
            {expanded
              ? 'Hide deployed artifact hashes'
              : 'Show deployed artifact hashes'}
          </button>

          {expanded && deployed ? (
            <div
              className="mt-2 bg-white/60 rounded px-2 py-2 text-xs font-mono space-y-0.5"
              data-testid="incident-drift-diagnostic"
            >
              <div>git_sha_full: {deployed.git_sha_full}</div>
              <div>
                panel_hash:{' '}
                {deployed.source_hashes?.['IncidentActionsPanel.jsx']}
              </div>
              <div>
                flows_hash:{' '}
                {deployed.source_hashes?.['Bundle131_132IncidentFlows.jsx']}
              </div>
              <div>
                registry_size: {deployed.incident_registry_size}
              </div>
              <div>
                asset_manifest: {deployed.asset_manifest_hash}
              </div>
              <Button
                onClick={copyDiag}
                variant="outline"
                size="sm"
                className="mt-2"
                data-testid="incident-drift-copy-btn"
              >
                <Copy className="h-3 w-3 mr-1" />
                Copy diagnostic JSON
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default IncidentDriftBanner;
